import React, { useMemo } from 'react';
import { Box } from '@podiumhq/design-system';
import { useTracking } from '@podiumhq/tracking';

import { useBusinessInfoContext } from 'contexts/BusinessContextProvider';

import {
  GoogleAutocompletePrediction,
  GoogleAutocompleteResultsList,
  GoogleAutocompleteWrapper,
  useGooglePlaceSearchResults,
  useKeyboardAutocompleteResults,
} from '../GoogleAutocomplete';

import { BusinessDetailField } from './BusinessDetailsField';

export const BusinessNameField = ({
  boxWrapperProps = {},
  idPrefix = 'Trial',
  placeholder = 'Business Name',
  ...inputProps
}) => {
  const {
    businessInfo = {},
    gmb = {},
    handleGmbPredictionSelection,
  } = useBusinessInfoContext() || {};
  const { name: businessName } = businessInfo;

  const {
    inputRef: inputNameRef,
    listRef,
    getOnKeyDown,
    onKeyDown: onNameKeyDown,
    setShouldHideResults,
    shouldHideResults,
  } = useKeyboardAutocompleteResults();
  const { hasNoResultsRef, placePredictions } = useGooglePlaceSearchResults({
    businessName,
    googleBusinessName: gmb.googleBusinessName,
    setShouldHideResults,
    shouldHideResults,
  });
  const { trackEvent } = useTracking();

  const hideResults = () => setShouldHideResults(true);
  const handlePredictionSelection = (prediction) => {
    trackEvent('trials.businessInfo.gmbClick', { prediction });
    handleGmbPredictionSelection &&
      handleGmbPredictionSelection(prediction, hideResults);
  };

  const displayPredictionResults = useMemo(
    () =>
      businessName?.length >= 2 &&
      !shouldHideResults &&
      !hasNoResultsRef.current,
    [businessName, hasNoResultsRef, shouldHideResults]
  );

  return (
    <Box position="relative" {...boxWrapperProps}>
      <BusinessDetailField
        autoComplete="off"
        businessFieldProps={{ customUserProperty: 'BusinessName' }}
        fieldName="name"
        id={`${idPrefix}-business-name-input`}
        inputRef={inputNameRef}
        onKeyDown={onNameKeyDown}
        placeholder={placeholder}
        {...inputProps}
      />

      {displayPredictionResults && (
        <GoogleAutocompleteWrapper
          hideResults={hideResults}
          id={`${idPrefix}-business-name-gmb-results-wrapper`}
        >
          {placePredictions?.length > 0 && (
            <GoogleAutocompleteResultsList role="listbox" ref={listRef}>
              {placePredictions.map((prediction) => (
                <GoogleAutocompletePrediction
                  key={prediction.placeId}
                  description={prediction?.businessAddress}
                  handlePredictionSelection={handlePredictionSelection}
                  onKeyDown={getOnKeyDown}
                  prediction={prediction}
                  title={prediction?.googleBusinessName}
                />
              ))}
            </GoogleAutocompleteResultsList>
          )}
        </GoogleAutocompleteWrapper>
      )}
    </Box>
  );
};
